import { useEffect } from "react";
import { useAuthStore } from "../stores/authStore";

declare global {
  interface Window {
    Tawk_API?: any;
    Tawk_LoadStart?: Date;
  }
}

/**
 * Loads the Tawk.to live-chat widget when VITE_TAWK_PROPERTY_ID and
 * VITE_TAWK_WIDGET_ID are set. No-ops otherwise (dev, preview, or any
 * environment where chat isn't configured).
 *
 * When a user is signed in, identifies them to Tawk so the agent dashboard
 * shows who is chatting (name, email, role) instead of "Anonymous".
 */
export default function TawkWidget() {
  const propertyId = import.meta.env.VITE_TAWK_PROPERTY_ID as string | undefined;
  const widgetId = import.meta.env.VITE_TAWK_WIDGET_ID as string | undefined;
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    if (!propertyId || !widgetId) return;
    if (document.querySelector('script[src*="embed.tawk.to"]')) return;

    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://embed.tawk.to/${propertyId}/${widgetId}`;
    script.charset = "UTF-8";
    script.setAttribute("crossorigin", "*");
    document.body.appendChild(script);
  }, [propertyId, widgetId]);

  useEffect(() => {
    if (!propertyId || !widgetId || !user) return;

    const fullName = user.full_name || `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() || user.email;
    const apply = () => {
      if (!window.Tawk_API) return false;
      try {
        if (typeof window.Tawk_API.setAttributes === "function") {
          window.Tawk_API.setAttributes(
            {
              name: fullName,
              email: user.email || "",
              role: user.role_display || user.role || "",
              user_id: String(user.id),
            },
            () => {}
          );
        }
        if (typeof window.Tawk_API.visitor === "object" || window.Tawk_API.visitor === undefined) {
          window.Tawk_API.visitor = { name: fullName, email: user.email || "" };
        }
      } catch {
        /* widget not yet ready - retry below */
      }
      return true;
    };

    if (!apply()) {
      const id = setInterval(() => { if (apply()) clearInterval(id); }, 500);
      const stop = setTimeout(() => clearInterval(id), 15000);
      return () => { clearInterval(id); clearTimeout(stop); };
    }
  }, [user, propertyId, widgetId]);

  return null;
}
